import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import { premiumService } from '../services/premium/PremiumService';
import { CreatePremiumPayload } from '../types/premium';

const premiumKeys = {
  list: (organizationId?: string) =>
    [DashboardQueryKey.PREMIUMS, organizationId ?? 'my'] as const,
  myPrice: (stationId: string) => [DashboardQueryKey.MY_STATION_PRICE, stationId] as const,
};

/** `organizationId` undefined = the caller's own organization. */
export function usePremiumsQuery(organizationId?: string) {
  return useQuery({
    queryKey: premiumKeys.list(organizationId),
    queryFn: () => premiumService.list(organizationId),
    retry: false,
  });
}

function useInvalidatePremiums(organizationId?: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: premiumKeys.list(organizationId) });
}

export function useCreatePremiumMutation(organizationId?: string) {
  const invalidate = useInvalidatePremiums(organizationId);
  return useMutation({
    mutationFn: (payload: CreatePremiumPayload) => premiumService.create(payload, organizationId),
    onSuccess: invalidate,
  });
}

export function useUpdatePremiumPriceMutation(organizationId?: string) {
  const invalidate = useInvalidatePremiums(organizationId);
  return useMutation({
    mutationFn: ({ id, pricePerKwh }: { id: string; pricePerKwh: number }) =>
      premiumService.updatePrice(id, pricePerKwh, organizationId),
    onSuccess: invalidate,
  });
}

export function useDeletePremiumMutation(organizationId?: string) {
  const invalidate = useInvalidatePremiums(organizationId);
  return useMutation({
    mutationFn: (id: string) => premiumService.remove(id, organizationId),
    onSuccess: invalidate,
  });
}

/** The signed-in driver's price at a station (organization premium or the published price). */
export function useMyStationPriceQuery(stationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: premiumKeys.myPrice(stationId ?? ''),
    queryFn: () => premiumService.myPrice(stationId as string),
    enabled: enabled && Boolean(stationId),
    staleTime: 60 * 1000,
  });
}
